// lib/koi-pond-v3/interaction/waterDisturbances.ts
//
// T11.2 — water disturbance system. A bounded pool of expanding ripple
// rings shared by every surface interaction: clicks (T11.2), the cursor
// wake (T11.3), and surfacing koi (T11.5). Each disturbance does two
// things:
//   1. Writes into the water shader's uDisturb* uniforms so the surface
//      normal tilts radially near the wavefront → the moon reflection
//      breaks locally where the ripple lands.
//   2. Drives a visible additive ring on the water.
// Clicks/koi-surfacings also flag `startle` so nearby koi flee.
//
// Bounded (8 slots, matching the shader array) — reused, never grows.

import {
  PlaneGeometry,
  ShaderMaterial,
  Mesh,
  Group,
  Color,
  Vector2,
  AdditiveBlending,
} from 'three';
import { palette } from '../palette';

const MAX = 8;
const DUR = 2.5; // seconds a disturbance lives (matches the shader's fade)
const STARTLE_R = 2.6; // koi within this of a startling disturbance flee
// T11.3 cursor-wake tuning. Round-2 visual pass: the cursor IS the hero's
// water interaction now (the DOM cursor overlay was removed), so the wake is
// pushed from "sensed not seen" to clearly present — more ripples, stronger
// reflection break, larger rings — while still reading as water, not UI.
const WAKE_MAX = 6; // cap concurrent wake ripples (leaves slots for clicks/koi)
const WAKE_STRENGTH = 0.42; // normal-perturbation: how hard the wake breaks the reflection
const WAKE_SIZE = 0.85;

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

type WaterUniforms = {
  uDisturbCount: { value: number };
  uDisturbPos: { value: Vector2[] };
  uDisturbAge: { value: number[] };
  uDisturbStr: { value: number[] };
};

interface Disturbance {
  x: number;
  z: number;
  start: number;
  strength: number;
  startle: boolean;
  active: boolean;
  wake: boolean; // age-tapered envelope (cursor wake) vs flat (click/koi)
  mesh: Mesh;
  mat: ShaderMaterial;
}

function makeRingMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: {
      uR: { value: 0 }, // 0..1 expansion
      uOpacity: { value: 0 },
      uColor: { value: new Color(palette.rimLight.hex) },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform float uR; uniform float uOpacity; uniform vec3 uColor;
      varying vec2 vUv;
      void main() {
        float r = length(vUv - 0.5) * 2.0;
        float ring = exp(-pow((r - uR) * 8.0, 2.0));
        float ring2 = exp(-pow((r - uR * 0.62) * 10.0, 2.0)) * 0.4;
        gl_FragColor = vec4(uColor, (ring + ring2) * uOpacity);
      }
    `,
  });
}

export function makeWaterDisturbances(water: WaterUniforms) {
  const group = new Group();
  const pool: Disturbance[] = [];
  let now = 0;

  for (let i = 0; i < MAX; i++) {
    const mat = makeRingMaterial();
    const mesh = new Mesh(new PlaneGeometry(5, 5), mat);
    mesh.rotation.x = -Math.PI / 2; // flat on the water
    mesh.visible = false;
    mesh.renderOrder = 2;
    group.add(mesh);
    pool.push({ x: 0, z: 0, start: 0, strength: 0, startle: false, active: false, wake: false, mesh, mat });
  }

  function place(d: Disturbance, x: number, z: number, strength: number, startle: boolean, size: number, wake: boolean) {
    d.x = x; d.z = z; d.strength = strength; d.startle = startle; d.wake = wake;
    d.start = now; d.active = true;
    d.mesh.position.set(x, 1.02, z);
    d.mesh.scale.setScalar(size);
    d.mesh.visible = true;
  }

  // click / koi-surface splash — flat strength, full-size ring. size scales
  // only the visible ring; strength scales the shader normal-perturbation.
  function add(x: number, z: number, strength: number, startle = false, size = 1) {
    let d = pool.find((p) => !p.active);
    if (!d) d = pool.reduce((a, b) => (a.start < b.start ? a : b)); // recycle oldest
    place(d, x, z, strength, startle, size, false);
  }

  // cursor wake — faint, capped to WAKE_MAX, age-tapered (below); never
  // evicts a click/koi disturbance (recycles the oldest WAKE at the cap).
  function addWake(x: number, z: number) {
    const wakes = pool.filter((p) => p.active && p.wake);
    let d: Disturbance | undefined;
    if (wakes.length >= WAKE_MAX) d = wakes.reduce((a, b) => (a.start < b.start ? a : b));
    else d = pool.find((p) => !p.active);
    if (!d) return; // pool saturated by clicks/koi → skip this wake drop
    place(d, x, z, WAKE_STRENGTH, false, WAKE_SIZE, true);
  }

  // wake envelope: faintest when newborn (quick ramp in ~0.18s), peak, then
  // fade over the life — so the trail tapers instead of all sitting at one
  // level. Short ramp-in so the ripple registers immediately under the cursor.
  function wakeEnvelope(age: number): number {
    return clamp01(age / 0.18) * Math.max(0, 1 - age / DUR);
  }

  function update(t: number) {
    now = t;
    let n = 0;
    for (const d of pool) {
      if (!d.active) continue;
      const age = t - d.start;
      if (age > DUR) {
        d.active = false;
        d.mesh.visible = false;
        continue;
      }
      const eff = d.wake ? d.strength * wakeEnvelope(age) : d.strength;
      // shader uniforms (front-packed). eff already carries the wake taper;
      // the shader applies its own (1-age/DUR) on top for the click/koi fade.
      water.uDisturbPos.value[n].set(d.x, d.z);
      water.uDisturbAge.value[n] = age;
      water.uDisturbStr.value[n] = eff;
      n++;
      // visible ring opacity. Wakes carry a small `eff` (≈0.2–0.4) which the
      // old `min(1, eff)` crushed to a ~11% ring — too faint. Boost the wake's
      // visible ring well above its displacement strength so the cursor reads
      // as clearly touching the water; clicks/koi already peak near eff=1.
      const k = age / DUR;
      const vis = d.wake ? Math.min(1, eff * 2.6) : Math.min(1, eff);
      d.mat.uniforms.uR.value = k;
      d.mat.uniforms.uOpacity.value = Math.max(0, 1 - k) * 0.72 * vis;
    }
    water.uDisturbCount.value = n;
  }

  // nearest active startling disturbance to a koi → an avoid pull, or null
  function startleAt(x: number, z: number): { x: number; z: number; strength: number } | null {
    let best: Disturbance | null = null;
    let bestD = STARTLE_R;
    for (const d of pool) {
      if (!d.active || !d.startle) continue;
      const dist = Math.hypot(d.x - x, d.z - z);
      if (dist < bestD) { bestD = dist; best = d; }
    }
    if (!best) return null;
    const prox = 1 - bestD / STARTLE_R;
    return { x: best.x, z: best.z, strength: prox * 1.3 };
  }

  function dispose() {
    for (const d of pool) {
      d.mat.dispose();
      d.mesh.geometry.dispose();
    }
  }

  return { object: group, add, addWake, update, startleAt, dispose };
}
